import React, {useEffect, useState} from 'react';
import Table from '../../../components/common/table/Table';
import {Column} from "../../common/table/TableSection";
import DateRangeFilter from '../../common/table/DateRangeFilter';
import {useNavigate} from "react-router-dom";
import styles from "./AdminWorkerRequestTable.module.css";
import {BillingTableResponseData} from "../../../types/admin";
import {PaymentSort, PaymentTarget} from "../../../types/contract";
import {adminBillingTableAPI} from "../../../services/admin";
import Button from "../../common/button/Button";

interface DateRange {
    start: Date | null;
    end: Date | null;
}

interface DateRanges {
    payday: DateRange;
}

//어드민 결제 테이블
const AdminBillingTable = () => {
    const navigate = useNavigate();
    const [data, setData] = useState<BillingTableResponseData[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchType, setSearchType] = useState<PaymentTarget>(PaymentTarget.ALL);
    const [pendingSearchTerm, setPendingSearchTerm] = useState('');
    //필터 초기값
    const [filters, setFilters] = useState<Record<string, any>>({
        status: ["납부", "미납"],
        payday: 'ALL',
        min: '',
        max: ''
    });

    //날짜 필터 값
    const [dateRanges, setDateRanges] = useState<DateRanges>({
        payday: {start: null, end: null}
    });

    //정렬
    const [sort, setSort] = useState<{ field: string | null, direction: 'asc' | 'desc' | null }>({
        field: 'companyName',
        direction: 'asc'
    });

    // 리셋 상태 추가
    const [isResetting, setIsResetting] = useState(false);

    // 필터 전체 초기화
    const resetAllFilters = () => {
        setIsResetting(true);

        // 필터 초기화
        setFilters({
            status: ["납부", "미납"],
            payday: 'ALL',
            min: '',
            max: ''
        });

        // 날짜 범위 초기화
        setDateRanges({
            payday: { start: null, end: null }
        });

        // 검색 관련 초기화
        setSearchTerm('');
        setPendingSearchTerm('');
        setSearchType(PaymentTarget.ALL)

        // 페이지 초기화
        setPage(1);

        // 정렬 초기화
        setSort({
            field: 'companyName',
            direction: 'asc'
        });

        // 다음 렌더링 사이클에서 리셋 상태 해제
        setTimeout(() => setIsResetting(false), 0);
    };

    //테이블 로우 선택 시
    const handleRowClick = (row: BillingTableResponseData) => {
        //세부 페이지로 이동
        navigate(`/admin/billing/payment/${row.paymentId}`);
    };

    //컬럼
    const columns: Column<BillingTableResponseData>[] = [
        {
            key: 'No',
            header: '결제 번호',
            width: '10%',
            render: (value, row, index) => ((page - 1) * 7) + index + 1
        },
        {
            key: 'companyName',
            header: '회사명',
            width: '20%',
            sortable: true
        },
        {
            key: 'orderName',
            header: '결제명',
            width: '20%',
            sortable: true
        },
        {
            key: 'amount',
            header: '결제액',
            width: '20%',
            sortable: true,
            render: value => `${value.toLocaleString()}원`
        },
        {
            key: 'paidAt',
            header: '결제일자',
            width: '20%',
            sortable: true,
            render: value => formatDate(value)
        },
        {
            key: 'status',
            header: '결제 상태',
            width: '10%',
            render: (value, row) => row.paidAt ? (row.canceledAt ? '납부(취소)' : '납부') : '미납'
        }
    ];

    //결제 상태 필터
    const statusFilterOptions = [
        {label: '납부', value: '납부'},
        {label: '미납', value: '미납'}
    ];

    //API 사용 위한 날짜 데이터 형식 변환
    const formatDateForAPI = (date: Date | string | null): string => {
        if (!date) return '';
        const dateObj = date instanceof Date ? date : new Date(date);

        if (isNaN(dateObj.getTime())) {
            return '-';
        }
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    //표시 위한 날짜 데이터 형식 변환
    const formatDate = (date: Date | null) => {
        //시간 제외, 없을 경우 '-'로 표시
        return date?.toString().split('T')[0] || "-";
    }

    //API 사용 위한 날짜 범위 데이터 형식 변환
    const formatDateRangeForAPI = (start: Date | null, end: Date | null): string | null => {
        //선택 안한 경우 'ALL'로 표시
        if (!start && !end) return 'ALL';
        if (!end) return `${formatDateForAPI(start)}~`;
        //시작날짜~종료날짜 형식으로 변환
        return `${formatDateForAPI(start)}~${formatDateForAPI(end)}`;
    };

    //날짜 형식 변환
    const handleDateRangeChange = (key: string, start: Date | null, end: Date | null) => {
        // 리셋 중일 때는 날짜 변경 로직 건너뛰기
        if (isResetting) return;

        setDateRanges(prev => ({
            ...prev,
            [key]: {start, end}
        }));

        const dateRangeString = formatDateRangeForAPI(start, end);
        setFilters(prev => ({
            ...prev,
            [key]: dateRangeString
        }));

        setPage(1);
    };

    //테이블 필터 설정
    const tableFilters = [
        {
            key: 'status',
            label: '처리 상태',
            options: statusFilterOptions,
            multiple: true,
            value: filters.status,
            component: undefined
        },
        {
            key: 'amount',
            label: '결제액',
            component: (
                <div className={styles.numberRangeFilter}>
                    <span>결제액: </span>
                    <div className={styles.rangeInput}>
                        <input
                            type="number"
                            min="0"
                            placeholder="최소"
                            value={filters.min === 0 ? '' : filters.min}
                            onChange={(e) => handleFilterChange('min', e.target.value)}
                            className={styles.numberInput}
                            title="최소 결제액"
                        />
                        <span className={styles.rangeSeparator}>~</span>
                        <input
                            type="number"
                            min="0"
                            placeholder="최대"
                            value={filters.max === 0 ? '' : filters.max}
                            onChange={(e) => handleFilterChange('max', e.target.value)}
                            className={styles.numberInput}
                            title="최대 결제액"
                        />
                    </div>
                </div>
            ),
            options: [],
            multiple: false,
            value: null
        },
        {
            key: 'payday',
            label: '결제일',
            component: (
                <DateRangeFilter
                    label="결제일"
                    startDate={dateRanges.payday.start}
                    endDate={dateRanges.payday.end}
                    onDateChange={(start, end) => handleDateRangeChange('payday', start, end)}
                    isOpen={false}
                    onToggle={() => {
                    }}
                />
            ),
            options: [],
            multiple: false,
            value: null
        }
    ];

    //정렬 기준 설정
    const getSortParam = (field: string | null, direction: 'asc' | 'desc' | null): PaymentSort => {
        if (!field || !direction) return PaymentSort.PAID_DESC;
        const sortMap: Record<string, Record<string, PaymentSort>> = {
            companyName: {
                asc: PaymentSort.COMPANY_ASC,
                desc: PaymentSort.COMPANY_DESC
            },
            paidAt: {
                asc: PaymentSort.PAID_ASC,
                desc: PaymentSort.PAID_DESC
            },
            amount: {
                asc: PaymentSort.AMOUNT_ASC,
                desc: PaymentSort.AMOUNT_DESC
            },
            orderName: {
                asc: PaymentSort.ORDER_ASC,
                desc: PaymentSort.ORDER_DESC
            }
        };
        return sortMap[field]?.[direction] || PaymentSort.PAID_DESC;
    };

    //검색
    const handleSearch = () => {
        setSearchTerm(pendingSearchTerm);
        setPage(1);
    };

    //엔터키 사용 검색
    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    //결제 납부 상태
    const getPaymentStatus = (status: string[]): boolean[] => {
        if (!status || status.length === 0) return [];
        return status
            .filter(item => item === "납부" || item === "미납")
            .map(item => item === "납부");
    };

    //테이블 데이터 설정
    const fetchData = async () => {
        try {
            setLoading(true);
            //결제 내역 데이터 API 호출
            const response = await adminBillingTableAPI({
                page: page - 1,
                sort: getSortParam(sort.field, sort.direction),
                search: searchTerm,
                target: searchType,
                min: filters.min,
                max: filters.max,
                paid: filters.payday,
                isPaid: getPaymentStatus(filters.status)
            });
            //데이터 설정
            setData(response.content);
            //페이지 설정
            setTotalPages(Math.ceil(response.total / 7));
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [page, searchTerm, sort, filters]);

    //검색 대상 설정
    const searchOptions = [
        {value: PaymentTarget.ALL, label: '전체'},
        {value: PaymentTarget.COMPANY_NAME, label: '회사명'},
        {value: PaymentTarget.ORDER_NAME, label: '주문명'}
    ];

    //필터값 변경
    const handleFilterChange = (key: string, value: any) => {
        setFilters(prev => {
            //계약 상태 필터 변경
            if (key === 'status') {
                const newStatuses = value;
                if (newStatuses.length === 0) {
                    return {...prev, [key]: ["납부", "미납"]};
                }
                return {...prev, status: newStatuses};
            }

            //결제액 필터 변경
            if (key === 'min' || key === 'max') {
                if (value === '' || /^\d+$/.test(value)) {
                    const numberValue = value === '' ? '' : parseInt(value);
                    if (key === 'min' && prev.max !== '' && numberValue > parseInt(prev.max)) {
                        return {...prev, min: numberValue, max: numberValue};
                    }
                    if (key === 'max' && prev.min !== '' && numberValue !== '' && numberValue < parseInt(prev.min)) {
                        return {...prev, min: numberValue, max: numberValue};
                    }
                    return {...prev, [key]: numberValue};
                }
                return prev;
            }

            return {...prev, [key]: value};
        });
        setPage(1);
    };

    //정렬 변경
    const handleSortChange = (field: string | null, direction: 'asc' | 'desc' | null) => {
        setSort({field, direction});
    };

    return (
        <div className={styles.container}>
            <div className={styles.searchContainer}>
                <h3>결제</h3>
                <div className={styles.searchInputContainer}>
                    <div>
                        {/*검색 대상 선택*/}
                        <select
                            value={searchType}
                            onChange={(e) => setSearchType(e.target.value as PaymentTarget)}
                            className={styles.searchTypeSelect}
                            title="검색 유형 선택"
                        >
                            {searchOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className={styles.searchInputWrapper}>
                        {/*검색 창*/}
                        <input
                            type="text"
                            placeholder="검색어를 입력하세요"
                            value={pendingSearchTerm}
                            onChange={(e) => setPendingSearchTerm(e.target.value)}
                            onKeyPress={handleKeyPress}
                            className={styles.searchInputBilling}
                            title="검색어 입력"
                        />
                        {/*검색 버튼*/}
                        <Button
                            onClick={handleSearch}
                            className={styles.searchButton}
                            variant="primary"
                            size={"small"}
                        >
                            검색
                        </Button>
                    </div>
                </div>
            </div>
            {/*테이블*/}
            <Table
                columns={columns}
                data={data}
                loading={loading}
                size="medium"
                filters={tableFilters}
                showPagination={true}
                page={page}
                pageSize={7}
                totalItems={totalPages * 7}
                onPageChange={setPage}
                onFilterChange={handleFilterChange}
                onSortChange={handleSortChange}
                onRowClick={handleRowClick}
                onReset={resetAllFilters}
            />
        </div>
    );
};

export default AdminBillingTable;